import classNames from 'classnames'
import { Fragment, useEffect, useState } from 'react'
import { Dialog, Menu, Popover, Transition } from '@headlessui/react'
import { Icon, IconType } from '../Icon'
import { Button, ButtonStyle, ButtonColor, ButtonSize } from '../Button'

interface IProps {
  query: string
  setQuery(value: string): void
  onSearch(): void
  filterOptions?: {
    id: string
    name: string
    options: {
      value: any
      label: string
      icon?: JSX.Element
    }[]
  }[]
  filtersValue?: any
  onFiltersUpdate?(section, option): void
  sortOptions?: {
    name: string
    value: any
  }[]
  onSort?(sort: any): void
  leftComponent?: React.ReactNode
  rightComponent?: React.ReactNode
  isSearchBarFullWidth?: boolean
  searchOnLeft?: boolean
  searchPlaceholder?: string
  className?: string
}

const ListOptions = ({
  query,
  setQuery,
  onSearch,
  filterOptions,
  filtersValue,
  onFiltersUpdate,
  sortOptions,
  onSort,
  leftComponent,
  rightComponent,
  isSearchBarFullWidth = false,
  searchOnLeft = false,
  searchPlaceholder = 'Search',
  className,
}: IProps) => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const handleEnterKeydown = async (e: KeyboardEvent): Promise<void> => {
      if (e.key === 'Enter') {
        onSearch()
      }
    }

    window.addEventListener('keydown', handleEnterKeydown)
    return () => window.removeEventListener('keydown', handleEnterKeydown)
  }, [onSearch])

  return (
    <div
      className={classNames(
        'flex flex-col md:flex-row justify-between items-center w-fullrounded-t',
        className,
      )}
    >
      <div
        className={`flex items-center ${searchOnLeft ? 'md:order-2' : 'md:order-1'} w-full md:w-auto mb-2 md:mb-0`}
      >
        {!filterOptions && leftComponent}

        {/* Mobile filter dialog */}
        {filterOptions && onFiltersUpdate && filtersValue && (
          <Transition.Root show={open} as={Fragment}>
            <Dialog as='div' className='relative z-40' onClose={setOpen}>
              <Transition.Child
                as={Fragment}
                enter='transition-opacity ease-linear duration-300'
                enterFrom='opacity-0'
                enterTo='opacity-100'
                leave='transition-opacity ease-linear duration-300'
                leaveFrom='opacity-100'
                leaveTo='opacity-0'
              >
                <div className='fixed inset-0 bg-black bg-opacity-25' />
              </Transition.Child>

              <div className='fixed inset-0 z-40 flex'>
                <Transition.Child
                  as={Fragment}
                  enter='transition ease-in-out duration-300 transform'
                  enterFrom='translate-x-full'
                  enterTo='translate-x-0'
                  leave='transition ease-in-out duration-300 transform'
                  leaveFrom='translate-x-0'
                  leaveTo='translate-x-full'
                >
                  <Dialog.Panel className='relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-6 shadow-xl'>
                    <div className='flex items-center justify-between px-4'>
                      <h2 className='text-lg font-medium text-gray-900'>
                        Filters
                      </h2>
                      <button
                        type='button'
                        className='-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500'
                        onClick={() => setOpen(false)}
                      >
                        <span className='sr-only'>Close menu</span>
                        <Icon
                          type={IconType.x}
                          className='h-6 w-6'
                          aria-hidden='true'
                        />
                      </button>
                    </div>

                    <form className='mt-4 px-4'>
                      {filterOptions.map((section) => (
                        <div
                          key={section.name}
                          className='border-t border-gray-200 py-6'
                        >
                          <h3 className='text-sm font-medium text-gray-900 mb-4'>
                            {section.name}
                          </h3>
                          <div className='space-y-4'>
                            {section.options.map((option, optionIdx) => (
                              <div
                                key={option.value}
                                className='flex items-center'
                              >
                                <input
                                  id={`filter-mobile-${section.id}-${optionIdx}`}
                                  name={`${section.id}[]`}
                                  type='checkbox'
                                  className='h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                                  checked={
                                    filtersValue[section.id][option.value]
                                  }
                                  onChange={() =>
                                    onFiltersUpdate(section, option)
                                  }
                                />
                                <label
                                  htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
                                  className='ml-3 text-sm text-gray-500'
                                >
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </form>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition.Root>
        )}

        {/* Sort dropdown */}
        {sortOptions && onSort && (
          <Menu as='div' className='relative inline-block text-left mr-2'>
            <Menu.Button className='group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900 h-9 border border-gray-200 bg-white px-4 py-2 shadow-sm hover:bg-gray-100 focus:outline-none rounded-md'>
              Sort
              <Icon
                type={IconType.chevronDown}
                className='-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500'
                aria-hidden='true'
              />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter='transition ease-out duration-100'
              enterFrom='transform opacity-0 scale-95'
              enterTo='transform opacity-100 scale-100'
              leave='transition ease-in duration-75'
              leaveFrom='transform opacity-100 scale-100'
              leaveTo='transform opacity-0 scale-95'
            >
              <Menu.Items className='absolute left-0 z-10 mt-2 w-40 origin-top-left rounded-md bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none'>
                <div className='py-1'>
                  {sortOptions.map((option) => (
                    <Menu.Item key={option.value}>
                      {({ active }) => (
                        <div
                          onClick={() => onSort(option.value)}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'cursor-pointer block px-4 py-2 text-sm font-medium text-gray-900',
                          )}
                        >
                          {option.name}
                        </div>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        )}

        {/* Single filter - show directly for mobile/tablet */}
        {filterOptions && filterOptions.length === 1 && (
          <Popover
            as='div'
            className='relative inline-block text-left mr-2 xl:hidden'
          >
            <Popover.Button className='group inline-flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 h-9 border border-gray-200 bg-white px-4 py-2 shadow-sm hover:bg-gray-100 focus:outline-none rounded-md'>
              <span>{filterOptions[0].name}</span>
              <span className='ml-1.5 rounded bg-gray-200 py-0.5 px-1.5 text-xs font-semibold tabular-nums text-gray-700'>
                {
                  Object.values(filtersValue[filterOptions[0].id]).filter(
                    (value) => value === true,
                  ).length
                }
              </span>
              <Icon
                type={IconType.chevronDown}
                className='-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500'
                aria-hidden='true'
              />
            </Popover.Button>
            <Transition
              as={Fragment}
              enter='transition ease-out duration-100'
              enterFrom='transform opacity-0 scale-95'
              enterTo='transform opacity-100 scale-100'
              leave='transition ease-in duration-75'
              leaveFrom='transform opacity-100 scale-100'
              leaveTo='transform opacity-0 scale-95'
            >
              <Popover.Panel className='absolute right-0 z-10 mt-2 origin-top-right rounded-md bg-white p-4 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none min-w-max'>
                <div className='space-y-4'>
                  {filterOptions[0].options.map((option, optionIdx) => (
                    <div key={option.value} className='flex items-center'>
                      <input
                        id={`filter-mobile-single-${filterOptions[0].id}-${optionIdx}`}
                        name={`${filterOptions[0].id}[]`}
                        type='checkbox'
                        className='h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                        checked={
                          filtersValue[filterOptions[0].id][option.value]
                        }
                        onChange={() =>
                          onFiltersUpdate(filterOptions[0], option)
                        }
                      />
                      <label
                        htmlFor={`filter-mobile-single-${filterOptions[0].id}-${optionIdx}`}
                        className='ml-3 whitespace-nowrap pr-6 text-sm font-medium text-gray-900'
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </Popover.Panel>
            </Transition>
          </Popover>
        )}

        {/* Multiple filters - show Filters button for mobile/tablet */}
        {filterOptions && filterOptions.length > 1 && (
          <button
            type='button'
            className='inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 xl:hidden h-9 border border-gray-200 bg-white px-4 py-2 shadow-sm hover:bg-gray-100 focus:outline-none rounded-md mr-2'
            onClick={() => setOpen(true)}
          >
            Filters
            {/* Show total active filters count */}
            {(() => {
              const totalActiveFilters = filterOptions.reduce(
                (total, section) =>
                  total +
                  Object.values(filtersValue[section.id]).filter(
                    (value) => value === true,
                  ).length,
                0,
              )
              return (
                totalActiveFilters > 0 && (
                  <span className='ml-1.5 rounded bg-gray-200 py-0.5 px-1.5 text-xs font-semibold tabular-nums text-gray-700'>
                    {totalActiveFilters}
                  </span>
                )
              )
            })()}
            <Icon
              type={IconType.chevronDown}
              className='-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500'
              aria-hidden='true'
            />
          </button>
        )}

        {/* Desktop filter dropdowns - only show first 3 */}
        <Popover.Group className='hidden xl:flex xl:items-baseline xl:space-x-2'>
          {filterOptions
            ?.slice(0, filterOptions.length === 1 ? 1 : 3)
            .map((section, sectionIdx) => (
              <Popover
                as='div'
                key={section.name}
                id={`desktop-menu-${sectionIdx}`}
                className='relative inline-block text-left'
              >
                <Popover.Button className='group inline-flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 h-9 border border-gray-200 bg-white px-4 py-2 shadow-sm hover:bg-gray-100 focus:outline-none rounded-md'>
                  <span>{section.name}</span>
                  <span className='ml-1.5 rounded bg-gray-200 py-0.5 px-1.5 text-xs font-semibold tabular-nums text-gray-700'>
                    {
                      Object.values(filtersValue[section.id]).filter(
                        (value) => value === true,
                      ).length
                    }
                  </span>
                  <Icon
                    type={IconType.chevronDown}
                    className='-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500'
                    aria-hidden='true'
                  />
                </Popover.Button>
                <Transition
                  as={Fragment}
                  enter='transition ease-out duration-100'
                  enterFrom='transform opacity-0 scale-95'
                  enterTo='transform opacity-100 scale-100'
                  leave='transition ease-in duration-75'
                  leaveFrom='transform opacity-100 scale-100'
                  leaveTo='transform opacity-0 scale-95'
                >
                  <Popover.Panel className='absolute right-0 z-10 mt-2 origin-top-right rounded-md bg-white p-4 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none min-w-max'>
                    <div className='space-y-4'>
                      {section.options.map((option, optionIdx) => (
                        <div key={option.value} className='flex items-center'>
                          <input
                            id={`filter-${section.id}-${optionIdx}`}
                            name={`${section.id}[]`}
                            type='checkbox'
                            className='h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                            checked={filtersValue[section.id][option.value]}
                            onChange={() => onFiltersUpdate(section, option)}
                          />
                          <label
                            htmlFor={`filter-${section.id}-${optionIdx}`}
                            className='ml-3 whitespace-nowrap pr-6 text-sm font-medium text-gray-900'
                          >
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </Popover.Panel>
                </Transition>
              </Popover>
            ))}

          {/* Show "More" button if there are more than 3 filters */}
          {filterOptions && filterOptions.length > 3 && (
            <button
              type='button'
              className='inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 h-9 border border-gray-200 bg-white px-4 py-2 shadow-sm hover:bg-gray-100 focus:outline-none rounded-md'
              onClick={() => setOpen(true)}
            >
              More
              {(() => {
                const remainingFilters = filterOptions.slice(3)
                const totalActiveInRemaining = remainingFilters.reduce(
                  (total, section) =>
                    total +
                    Object.values(filtersValue[section.id]).filter(
                      (value) => value === true,
                    ).length,
                  0,
                )
                return (
                  totalActiveInRemaining > 0 && (
                    <span className='ml-1.5 rounded bg-gray-200 py-0.5 px-1.5 text-xs font-semibold tabular-nums text-gray-700'>
                      {totalActiveInRemaining}
                    </span>
                  )
                )
              })()}
              <Icon
                type={IconType.chevronDown}
                className='-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500'
                aria-hidden='true'
              />
            </button>
          )}
        </Popover.Group>

        {/* OLD FILTER CODE COMMENTED - DO NOT DELETE! */}
        {/* {filterOptions?.map((section) => (
          <div key={section.id}>
            {section.options.map((option, optionIdx) => (
              <button
                key={optionIdx}
                type='button'
                className={classNames(
                  'h-9 w-fit order-2 inline-flex items-center border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none',
                  optionIdx === 0
                    ? 'rounded-md rounded-tr-none rounded-br-none'
                    : null,
                  optionIdx === section.options.length - 1
                    ? 'rounded-md rounded-tl-none rounded-bl-none'
                    : null,
                  optionIdx !== 0 && optionIdx !== section.options.length
                    ? 'border-y'
                    : null,
                  filtersValue[section.id][option.value] === true
                    ? 'bg-[#e2e8f0] hover:bg-gray-100'
                    : null,
                )}
                onClick={() => onFiltersUpdate(section, option)}
              >
                {option.label}
                <span className='ml-1'>{option.icon}</span>
              </button>
            ))}
          </div>
        ))} */}
      </div>

      <div
        className={classNames(
          `${searchOnLeft ? 'md:order-1' : 'md:order-2'} w-full `,
          isSearchBarFullWidth ? 'w-full' : 'md:w-[400px]',
        )}
      >
        <div className='flex items-center'>
          <label className='sr-only text-gray-500'>Search</label>

          <div className='relative w-full'>
            <div className='flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none'>
              <Icon
                type={IconType.search}
                className='mr-3 h-5 w-5 text-gray-500'
                aria-hidden='true'
              />
            </div>

            <input
              type='text'
              id='campaign-search'
              // className='bg-white border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-blue-500 block w-full pl-10 p-2.5  dark:bg-gray-700 dark:placeholder-gray-500 dark:text-white dark:focus:ring-blue-500 focus:outline-none'
              className='bg-white border border-gray-200 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-0 focus:border-gray-500 focus:ring-gray-500 hover:border-gray-500 hover:ring-gray-500 active:border-gray-500 active:ring-gray-500 block w-full pl-10 p-2.5 [&:-webkit-autofill]:bg-white [&:-webkit-autofill]:shadow-[0_0_0_1000px_white_inset] [&:-webkit-autofill]:[transition:background-color_9999s_ease-in-out_0s]'
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              required
            />
            {query && (
              <Button
                type='button'
                styleType={ButtonStyle.ghost}
                colour={ButtonColor.lightGray}
                size={ButtonSize.small}
                className='absolute inset-y-0 right-0 flex items-center pr-3'
                onClick={() => setQuery('')}
                icon={
                  <Icon
                    type={IconType.x}
                    className='h-5 w-5 text-gray-400 hover:text-gray-500'
                    aria-hidden='true'
                  />
                }
                title=''
              />
            )}
          </div>
        </div>
      </div>

      {rightComponent && (
        <div className='md:order-3 w-full md:w-auto'>{rightComponent}</div>
      )}
    </div>
  )
}

export { ListOptions }
